import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateRunner, type PlainRunner } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useId, useState, type FC, type FormEvent } from "react";

type Props = {
    runner: PlainRunner;
    open: boolean;
    onClose: () => void;
};

export const RenameRunnerModal: FC<Props> = ({ open, runner, onClose }) => {
    const { toast } = useToast();
    const updateRunner = useUpdateRunner();

    const [newName, setNewName] = useState<string>();
    const name = newName || runner.name;

    const inputNameID = useId();

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!name) {
                return;
            }

            try {
                await updateRunner.mutateAsync({
                    name,
                    runnerId: runner.runnerId,
                });

                onClose();
            } catch (error) {
                toast({
                    title: "We couldn't rename your runner. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [name, updateRunner, runner.runnerId, onClose, toast],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-[400px]" data-track-location={TrackLocations.RenameRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Rename runner</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <DialogBody>
                        <div className="flex flex-col gap-2">
                            <div className="space-y-1">
                                <Label htmlFor={inputNameID}>Name</Label>
                                <Input
                                    id={inputNameID}
                                    type="text"
                                    name="runner_name"
                                    value={name || ""}
                                    onChange={(e) => setNewName(e.target.value)}
                                />
                            </div>
                        </div>
                    </DialogBody>

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            loading={updateRunner.isPending}
                            autoFocus={true}
                            type="submit"
                            disabled={!name}
                            variant="brand"
                            data-tracking-id="confirm-rename-runner-rename-runner-modal"
                        >
                            Rename
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
